import { useEffect, useState, type ReactElement } from 'react';
import { Button, Input, toast } from '@ponzinomics/ui';
import type { Quest } from '@ponzinomics/types';
import { Link } from 'react-router-dom';
import {
  useCreateQuest,
  useCurrentProject,
  useDashboardStats,
  useDeleteQuest,
  useLeaderboard,
  useProjects,
  useQuests,
  useUpdateQuest,
} from '../../hooks';
import { useLeaderboardConfigs } from '../../hooks/use-leaderboard-configs';
import { useProjectStore } from '../../stores';

interface LoopStep {
  title: string;
  description: string;
  href: string;
  complete: boolean;
  cta: string;
}

interface SurfaceCard {
  title: string;
  description: string;
  href: string;
  metric: string;
  status: string;
  Icon: ({ className }: { className?: string }) => ReactElement;
  colorClass: string;
}

type QuickQuestType =
  | 'twitter_follow'
  | 'twitter_retweet'
  | 'wallet_connect'
  | 'token_hold'
  | 'custom';
type QuickQuestStatus = 'draft' | 'active';

const QUEST_TYPE_LABELS: Record<QuickQuestType, string> = {
  twitter_follow: 'Follow accounts',
  twitter_retweet: 'Retweet post',
  wallet_connect: 'Connect wallet',
  token_hold: 'Hold token',
  custom: 'Custom action',
};

const QUEST_TYPE_PLACEHOLDERS: Record<QuickQuestType, string> = {
  twitter_follow: 'ponzinomics_io',
  twitter_retweet: 'https://x.com/yourproject/status/1234567890',
  wallet_connect: '0x...',
  token_hold: 'TOKEN_ADDRESS',
  custom: 'Describe the action requirement',
};

const QUEST_TYPE_REQUIREMENT_LABELS: Record<QuickQuestType, string> = {
  twitter_follow: 'Account Handle',
  twitter_retweet: 'Post URL',
  wallet_connect: 'Wallet Address',
  token_hold: 'Token Contract',
  custom: 'Requirement',
};

const QUEST_TYPE_TITLE_PLACEHOLDERS: Record<QuickQuestType, string> = {
  twitter_follow: 'Follow the project account',
  twitter_retweet: 'Retweet launch post',
  wallet_connect: 'Connect your wallet',
  token_hold: 'Hold TOKEN to earn',
  custom: 'Complete the action',
};

export function PonzinomicsOverviewPage(): ReactElement {
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { project } = useCurrentProject();
  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  const { data: quests } = useQuests();
  const createQuest = useCreateQuest();
  const updateQuest = useUpdateQuest();
  const deleteQuest = useDeleteQuest();
  const {
    configs: leaderboardConfigs,
    isLoading: leaderboardLoading,
    createConfig,
    isCreating: isCreatingLeaderboard,
    updateConfig,
    isUpdating: isUpdatingLeaderboard,
  } = useLeaderboardConfigs();
  const { data: dashboardStats, isLoading: dashboardLoading } = useDashboardStats();
  const [questType, setQuestType] = useState<QuickQuestType>('twitter_follow');
  const [questStatus, setQuestStatus] = useState<QuickQuestStatus>('active');
  const [questTitle, setQuestTitle] = useState('');
  const [questDescription, setQuestDescription] = useState('');
  const [questTarget, setQuestTarget] = useState('');
  const [questPoints, setQuestPoints] = useState(100);
  const [lastCreatedQuestId, setLastCreatedQuestId] = useState<string | null>(null);
  const [editingQuestId, setEditingQuestId] = useState<string | null>(null);
  const [editPoints, setEditPoints] = useState<number>(0);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [confirmDeleteQuestId, setConfirmDeleteQuestId] = useState<string | null>(null);
  const [questSearchQuery, setQuestSearchQuery] = useState('');
  const [showCampaignCreator, setShowCampaignCreator] = useState(false);

  const activeQuestCount = (quests ?? []).filter((quest) => quest.status === 'active').length;
  const activeLeaderboardCount = leaderboardConfigs.filter((config) => config.isActive).length;
  const { data: leaderboardSnapshot, isLoading: leaderboardDataLoading } =
    useLeaderboard('all-time');
  const topPerformers = leaderboardSnapshot?.entries?.slice(0, 5) ?? [];
  const totalParticipants = leaderboardSnapshot?.totalParticipants ?? 0;

  const totalQuestCompletions = (quests ?? []).reduce(
    (sum, quest) => sum + quest.completionCount,
    0
  );
  const avgCompletionsPerQuest =
    quests && quests.length > 0 ? Math.round(totalQuestCompletions / quests.length) : 0;
  const totalQuestPointsOffered = (quests ?? []).reduce(
    (sum, quest) => sum + quest.points * quest.completionCount,
    0
  );
  const topQuestsByCompletions = [...(quests ?? [])]
    .sort((a, b) => b.completionCount - a.completionCount)
    .slice(0, 3);
  const underperformingQuests = (quests ?? []).filter(
    (quest) => quest.status === 'active' && quest.completionCount === 0
  );
  const questTypeBreakdown = (quests ?? []).reduce<Record<string, number>>((acc, quest) => {
    acc[quest.type] = (acc[quest.type] ?? 0) + 1;
    return acc;
  }, {});

  const inactiveLeaderboard =
    activeLeaderboardCount === 0
      ? (leaderboardConfigs.find((config) => !config.isActive) ?? null)
      : null;
  const leaderboardDisplayLimit = project?.settings.leaderboard.showTopCount ?? 100;
  const leaderboardResetCadence =
    project?.settings.leaderboard.resetPeriod === 'never'
      ? 'All time'
      : (project?.settings.leaderboard.resetPeriod ?? 'All time');
  const quickQuestPlaceholder = QUEST_TYPE_PLACEHOLDERS[questType];
  const canCreateQuest =
    Boolean(project) &&
    questTitle.trim().length > 0 &&
    questTarget.trim().length > 0 &&
    questPoints > 0;
  const leaderboardActionLabel =
    activeLeaderboardCount > 0
      ? 'Leaderboard live'
      : inactiveLeaderboard
        ? 'Reactivate existing board'
        : 'Create default leaderboard';
  const isLeaderboardActionPending = isCreatingLeaderboard || isUpdatingLeaderboard;

  useEffect(() => {
    setQuestType(project?.twitter ? 'twitter_follow' : 'twitter_retweet');
    setQuestTarget(project?.twitter ? project.twitter.replace(/^@/, '') : '');
    setQuestTitle('');
    setQuestDescription('');
    setQuestPoints(100);
    setQuestStatus('active');
    setLastCreatedQuestId(null);
  }, [project?.id, project?.twitter]);

  const loopSteps: LoopStep[] = [
    {
      title: 'Project Context',
      description: project
        ? `Current workspace is ${project.name}`
        : 'Choose the project that should receive the quest and reward loop',
      href: '/projects',
      complete: Boolean(project),
      cta: project ? 'Manage project' : 'Select project',
    },
    {
      title: 'Quest Engine',
      description:
        activeQuestCount > 0
          ? `${activeQuestCount} active quest${activeQuestCount === 1 ? '' : 's'} driving participation`
          : 'Define at least one live task so users can start earning points',
      href: '/ponzinomics/quests',
      complete: activeQuestCount > 0,
      cta: activeQuestCount > 0 ? 'Tune quests' : 'Create quest',
    },
    {
      title: 'Leaderboard Visibility',
      description:
        activeLeaderboardCount > 0
          ? `${activeLeaderboardCount} active leaderboard${activeLeaderboardCount === 1 ? '' : 's'} publishing rankings`
          : 'Activate a leaderboard so progress becomes visible and competitive',
      href: '/ponzinomics/leaderboards',
      complete: activeLeaderboardCount > 0,
      cta: activeLeaderboardCount > 0 ? 'Review leaderboards' : 'Create leaderboard',
    },
  ];

  const completedSteps = loopSteps.filter((step) => step.complete).length;
  const readinessScore = Math.round((completedSteps / loopSteps.length) * 100);

  const surfaces: SurfaceCard[] = [
    {
      title: 'Quests',
      description: 'Drive participation with task-based actions and reward hooks.',
      href: '/ponzinomics/quests',
      metric: activeQuestCount > 0 ? `${activeQuestCount} live` : 'No live quests',
      status: activeQuestCount > 0 ? 'Active' : 'Needs setup',
      Icon: QuestIcon,
      colorClass: 'text-emerald-400',
    },
    {
      title: 'Leaderboards',
      description: 'Publish visible rankings so users can see momentum and compete.',
      href: '/ponzinomics/leaderboards',
      metric:
        activeLeaderboardCount > 0
          ? `${activeLeaderboardCount} active config${activeLeaderboardCount === 1 ? '' : 's'}`
          : 'No active boards',
      status: activeLeaderboardCount > 0 ? 'Publishing' : 'Needs setup',
      Icon: LeaderboardIcon,
      colorClass: 'text-yellow-400',
    },
  ];

  if (projectsLoading && !project) {
    return (
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-white/[0.06]" />
          <div className="h-4 w-80 animate-pulse rounded bg-white/[0.04]" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="h-72 animate-pulse rounded-2xl border border-white/[0.06] bg-white/[0.03]" />
          <div className="h-72 animate-pulse rounded-2xl border border-white/[0.06] bg-white/[0.03]" />
        </div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Ponzinomics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Build the quest, reward, leaderboard, and amplification loop for each project.
          </p>
        </div>

        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-8 backdrop-blur-md">
          <div className="max-w-xl">
            <div className="mb-4 inline-flex rounded-full border border-white/[0.08] px-3 py-1 text-xs uppercase tracking-[0.24em] text-[#c4f70e]">
              Project Required
            </div>
            <h2 className="text-2xl font-semibold text-white">
              Start by creating your first project
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-400">
              The Ponzinomics loop is project-scoped. Once a project exists, this workspace can
              activate quests, rankings, and raiding from one control plane.
            </p>
            <div className="mt-6">
              <Link
                to="/projects"
                className="hover:bg-[#c4f70e]/16 inline-flex items-center gap-2 rounded-xl border border-[#c4f70e]/30 bg-[#c4f70e]/10 px-4 py-2 text-sm font-medium text-[#d7ff64] transition-colors hover:border-[#c4f70e]/50"
              >
                Create or select a project
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleQuickQuestCreate = async (): Promise<void> => {
    if (!project || !canCreateQuest) {
      toast({ title: 'Complete the quest fields first', variant: 'destructive' });
      return;
    }

    const requirements = [{ type: questType, value: questTarget.trim().replace(/^@/, '') }];

    if (requirements.length === 0 || !requirements[0]?.value) {
      toast({ title: 'Add at least one verification target', variant: 'destructive' });
      return;
    }

    const defaultDescriptions: Record<QuickQuestType, string> = {
      twitter_follow: 'Follow the selected project accounts to earn points.',
      twitter_retweet: 'Retweet the selected post to earn points.',
      wallet_connect: 'Connect your wallet to participate.',
      token_hold: 'Hold the required token to earn points.',
      custom: 'Complete the action to earn points.',
    };

    try {
      const createdQuest = await createQuest.mutateAsync({
        title: questTitle.trim(),
        description: questDescription.trim() || defaultDescriptions[questType],
        type: questType,
        points: questPoints,
        status: questStatus,
        requirements,
      });

      if (createdQuest?.id) {
        setLastCreatedQuestId(createdQuest.id);
      }

      toast({
        title: `Quest ${questStatus === 'active' ? 'launched' : 'created'}`,
        description:
          questStatus === 'active'
            ? 'The quest is now live and accepting completions.'
            : 'The quest is saved as a draft. Activate it when ready.',
      });
      setQuestTitle('');
      setQuestDescription('');
      if (questType === 'twitter_retweet') {
        setQuestTarget('');
      }
    } catch {
      toast({ title: 'Failed to create quest', variant: 'destructive' });
    }
  };

  const handleLeaderboardActivation = async (): Promise<void> => {
    if (!project) {
      toast({ title: 'Select a project first', variant: 'destructive' });
      return;
    }

    if (activeLeaderboardCount > 0) {
      toast({ title: 'A leaderboard is already active' });
      return;
    }

    try {
      if (inactiveLeaderboard) {
        await updateConfig({ configId: inactiveLeaderboard.id, dto: { isActive: true } });
        toast({ title: 'Leaderboard reactivated' });
      } else {
        await createConfig({
          name: `${project.name} Points Leaders`,
          metricField: 'points',
          sortOrder: 'desc',
          displayLimit: project.settings.leaderboard.showTopCount,
          timeWindow: mapResetPeriodToTimeWindow(project.settings.leaderboard.resetPeriod),
          isActive: true,
        });
        toast({ title: 'Leaderboard created and activated' });
      }
    } catch {
      toast({ title: 'Failed to activate leaderboard', variant: 'destructive' });
    }
  };

  const handleQuickQuestStatusToggle = async (
    questId: string,
    newStatus: 'draft' | 'active'
  ): Promise<void> => {
    try {
      await updateQuest.mutateAsync({
        id: questId,
        data: { status: newStatus },
      });

      toast({
        title: newStatus === 'active' ? 'Quest activated' : 'Quest paused',
        description:
          newStatus === 'active'
            ? 'The quest is now live and accepting completions.'
            : 'The quest is now paused. Users cannot complete it until reactivated.',
      });

      if (newStatus === 'active') {
        setQuestStatus('active');
      }
    } catch {
      toast({ title: 'Failed to update quest status', variant: 'destructive' });
    }
  };

  const startEditingQuest = (quest: Quest): void => {
    setEditingQuestId(quest.id);
    setEditPoints(quest.points);
    setEditingTitle(quest.title);
  };

  const cancelEditingQuest = (): void => {
    setEditingQuestId(null);
    setEditPoints(0);
    setEditingTitle('');
  };

  const handleSaveQuestEdit = async (questId: string): Promise<void> => {
    if (editPoints <= 0) {
      toast({ title: 'Points must be greater than 0', variant: 'destructive' });
      return;
    }

    try {
      await updateQuest.mutateAsync({
        id: questId,
        data: {
          points: editPoints,
          ...(editingTitle.trim().length > 0 && { title: editingTitle.trim() }),
        },
      });

      toast({ title: 'Quest updated' });
      cancelEditingQuest();
    } catch {
      toast({ title: 'Failed to update quest', variant: 'destructive' });
    }
  };

  const requestDeleteQuest = (questId: string): void => {
    setConfirmDeleteQuestId(questId);
  };

  const cancelDeleteQuest = (): void => {
    setConfirmDeleteQuestId(null);
  };

  const handleDeleteQuest = async (questId: string): Promise<void> => {
    try {
      await deleteQuest.mutateAsync(questId);
      toast({ title: 'Quest deleted' });
      if (editingQuestId === questId) {
        cancelEditingQuest();
      }
      setConfirmDeleteQuestId(null);
    } catch {
      toast({ title: 'Failed to delete quest', variant: 'destructive' });
    }
  };

  const recentQuests = (quests ?? [])
    .filter((quest) => {
      if (!questSearchQuery.trim()) return true;
      const q = questSearchQuery.toLowerCase();
      return (
        quest.title.toLowerCase().includes(q) ||
        quest.type.toLowerCase().includes(q) ||
        quest.status.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-3 inline-flex rounded-full border border-white/[0.08] px-3 py-1 text-xs uppercase tracking-[0.24em] text-[#c4f70e]">
            Operator Loop
          </div>
          <h1 className="text-3xl font-semibold text-white">Ponzinomics Control Plane</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">
            Run the growth loop from project setup through quests, rankings, and social
            amplification without leaving the operator surface.
          </p>
        </div>

        <div className="w-full max-w-sm">
          <label
            htmlFor="project-selector"
            className="mb-2 block text-xs uppercase tracking-[0.2em] text-gray-500"
          >
            Current Project
          </label>
          <select
            id="project-selector"
            value={project?.id ?? ''}
            onChange={(event) => {
              const nextProject =
                projects.find((candidate) => candidate.id === event.target.value) ?? null;
              setCurrentProject(nextProject);
            }}
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition-colors hover:border-white/[0.16] focus:border-[#c4f70e]/40"
          >
            {projects.map((candidate) => (
              <option key={candidate.id} value={candidate.id} className="bg-[#0d1117]">
                {candidate.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="notch-border-lg notch-border-white/8 relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(196,247,14,0.18),transparent_40%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-6 backdrop-blur-md">
          <div className="absolute inset-y-0 right-0 w-1/3 bg-[linear-gradient(180deg,rgba(196,247,14,0.08),transparent)]" />
          <div className="relative">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-[#c4f70e]/25 bg-[#c4f70e]/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[#d7ff64]">
                {project?.plan ?? 'free'} plan
              </span>
              <span className="rounded-full border border-white/[0.08] px-3 py-1 text-xs uppercase tracking-[0.2em] text-gray-400">
                {completedSteps}/{loopSteps.length} steps live
              </span>
            </div>
            <h2 className="text-2xl font-semibold text-white">{project?.name}</h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-gray-300">
              {project?.description ||
                'Use this project as the operating context for quests, points, rankings, and distribution rails.'}
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <MetricTile label="Readiness" value={`${readinessScore}%`} hint="Loop completion" />
              <MetricTile
                label="Active Quests"
                value={
                  dashboardLoading
                    ? '...'
                    : String(dashboardStats?.activeQuests ?? activeQuestCount)
                }
                hint="Current participation rails"
              />
              <MetricTile
                label="Participants"
                value={dashboardLoading ? '...' : String(dashboardStats?.totalUsers ?? 0)}
                hint="Leaderboard reach"
              />
            </div>
          </div>
        </div>

        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Loop Checklist</h3>
              <p className="mt-1 text-sm text-gray-500">Prioritize the next blocking step.</p>
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500">Live map</span>
          </div>

          <div className="space-y-3">
            {loopSteps.map((step) => (
              <Link
                key={step.title}
                to={step.href}
                className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 transition-colors hover:border-white/[0.12] hover:bg-white/[0.04]"
              >
                <div
                  className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border text-xs ${
                    step.complete
                      ? 'bg-emerald-400/12 border-emerald-400/30 text-emerald-300'
                      : 'border-amber-400/25 bg-amber-400/10 text-amber-200'
                  }`}
                >
                  {step.complete ? (
                    <CheckIcon className="h-3.5 w-3.5" />
                  ) : (
                    <PulseIcon className="h-3.5 w-3.5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-white">{step.title}</span>
                    <span className="text-xs text-gray-500">{step.cta}</span>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-gray-400">{step.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-emerald-300">
                Quest Quick Create
              </div>
              <h3 className="text-lg font-semibold text-white">
                Create the next participation rail
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Ship one concrete quest here instead of bouncing into a separate setup page.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!showCampaignCreator && (
                <button
                  type="button"
                  onClick={() => setShowCampaignCreator(true)}
                  className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-cyan-300"
                >
                  Campaign mode
                </button>
              )}
              <Link
                to="/ponzinomics/quests"
                className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-white"
              >
                Open quests
              </Link>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="flex flex-wrap gap-2">
              {(Object.keys(QUEST_TYPE_LABELS) as QuickQuestType[]).map((type) => {
                const active = questType === type;
                const color =
                  type === 'twitter_follow'
                    ? 'sky'
                    : type === 'twitter_retweet'
                      ? 'orange'
                      : type === 'wallet_connect'
                        ? 'violet'
                        : type === 'token_hold'
                          ? 'emerald'
                          : 'gray';

                const activeClasses: Record<string, string> = {
                  sky: 'bg-sky-400/12 border-sky-400/30 text-sky-300',
                  orange: 'bg-orange-400/12 border-orange-400/30 text-orange-300',
                  blue: 'bg-blue-400/12 border-blue-400/30 text-blue-300',
                  violet: 'bg-violet-400/12 border-violet-400/30 text-violet-300',
                  emerald: 'bg-emerald-400/12 border-emerald-400/30 text-emerald-300',
                  gray: 'bg-gray-400/12 border-gray-400/30 text-gray-300',
                };

                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setQuestType(type)}
                    className={`rounded-xl border px-3 py-2 text-xs font-medium uppercase tracking-[0.16em] transition-colors ${
                      active
                        ? activeClasses[color]
                        : 'border-white/[0.08] bg-white/[0.03] text-gray-400 hover:border-white/[0.16] hover:text-white'
                    }`}
                  >
                    {QUEST_TYPE_LABELS[type]}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-[0.18em] text-gray-500">Status:</span>
              <button
                type="button"
                onClick={() => setQuestStatus('active')}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium uppercase tracking-[0.16em] transition-colors ${
                  questStatus === 'active'
                    ? 'bg-emerald-400/12 border-emerald-400/30 text-emerald-300'
                    : 'border-white/[0.08] bg-white/[0.03] text-gray-400 hover:border-white/[0.16] hover:text-white'
                }`}
              >
                Launch live
              </button>
              <button
                type="button"
                onClick={() => setQuestStatus('draft')}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium uppercase tracking-[0.16em] transition-colors ${
                  questStatus === 'draft'
                    ? 'bg-gray-400/12 border-gray-400/30 text-gray-300'
                    : 'border-white/[0.08] bg-white/[0.03] text-gray-400 hover:border-white/[0.16] hover:text-white'
                }`}
              >
                Save draft
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-[1.4fr_0.6fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-gray-500">
                    Quest Title
                  </label>
                  <Input
                    value={questTitle}
                    onChange={(event) => setQuestTitle(event.target.value)}
                    placeholder={QUEST_TYPE_TITLE_PLACEHOLDERS[questType]}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-gray-500">
                    Description
                  </label>
                  <textarea
                    value={questDescription}
                    onChange={(event) => setQuestDescription(event.target.value)}
                    rows={3}
                    placeholder="Give operators and users one clear sentence about the action."
                    className="w-full resize-none rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-500 hover:border-white/[0.16] focus:border-[#c4f70e]/40"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-gray-500">
                    {QUEST_TYPE_REQUIREMENT_LABELS[questType]}
                  </label>
                  <Input
                    value={questTarget}
                    onChange={(event) => setQuestTarget(event.target.value)}
                    placeholder={quickQuestPlaceholder}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-gray-500">
                    Reward Points
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={String(questPoints)}
                    onChange={(event) => setQuestPoints(Number(event.target.value) || 0)}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[50, 100, 200].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setQuestPoints(preset)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                        questPoints === preset
                          ? 'bg-[#c4f70e] text-black'
                          : 'bg-white/[0.05] text-gray-400 hover:bg-white/[0.1] hover:text-white'
                      }`}
                    >
                      {preset} pts
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-white/[0.06] pt-4 md:flex-row md:items-center md:justify-between">
              <p className="text-xs leading-5 text-gray-500">
                This creates the quest {questStatus === 'active' ? 'live' : 'as a draft'} directly
                from the control plane so the loop starts here, not in a side page.
              </p>
              <Button
                onClick={handleQuickQuestCreate}
                disabled={!canCreateQuest || createQuest.isPending}
              >
                {createQuest.isPending
                  ? 'Creating...'
                  : questStatus === 'active'
                    ? 'Launch Quest'
                    : 'Save Draft'}
              </Button>
            </div>

            {lastCreatedQuestId && (
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-400/12 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-emerald-400/30 text-emerald-300">
                    <CheckIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">Quest action ready</div>
                    <p className="mt-1 text-xs leading-5 text-gray-300">
                      {quests?.find((q) => q.id === lastCreatedQuestId)?.status === 'active'
                        ? 'This quest is live and accepting completions.'
                        : 'This quest is saved as a draft. Activate it when ready.'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {quests?.find((q) => q.id === lastCreatedQuestId)?.status !== 'active' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleQuickQuestStatusToggle(lastCreatedQuestId, 'active')}
                          disabled={updateQuest.isPending}
                        >
                          {updateQuest.isPending ? 'Activating...' : 'Activate Now'}
                        </Button>
                      )}
                      {quests?.find((q) => q.id === lastCreatedQuestId)?.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickQuestStatusToggle(lastCreatedQuestId, 'draft')}
                          disabled={updateQuest.isPending}
                        >
                          {updateQuest.isPending ? 'Pausing...' : 'Pause Quest'}
                        </Button>
                      )}
                      <Link
                        to="/ponzinomics/quests"
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-400 transition-colors hover:text-white"
                      >
                        View all quests
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showCampaignCreator && (
          <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <div className="mb-2 inline-flex rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-cyan-300">
                  Campaign Mode
                </div>
                <h3 className="text-lg font-semibold text-white">Launch Multiple Quests</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Create 2-4 quests at once as a coordinated campaign. All quests launch live.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCampaignCreator(false)}
                className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-white"
              >
                Close
              </button>
            </div>

            <CampaignCreator
              onCancel={() => setShowCampaignCreator(false)}
              onCreated={() => setShowCampaignCreator(false)}
            />
          </div>
        )}

        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5">
            <div className="mb-2 inline-flex rounded-full border border-yellow-400/20 bg-yellow-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-yellow-200">
              Leaderboard Visibility
            </div>
            <h3 className="text-lg font-semibold text-white">Make progress visible</h3>
            <p className="mt-1 text-sm text-gray-500">
              Remove the ranking blocker with a default points board for the current project.
            </p>
          </div>

          <div className="space-y-3">
            <OperatorStatusRow
              label="Current status"
              value={
                activeLeaderboardCount > 0
                  ? `${activeLeaderboardCount} active board${activeLeaderboardCount === 1 ? '' : 's'}`
                  : inactiveLeaderboard
                    ? 'Saved board is inactive'
                    : 'No leaderboard exists yet'
              }
            />
            <OperatorStatusRow label="Default metric" value="Points, descending" />
            <OperatorStatusRow label="Display size" value={`Top ${leaderboardDisplayLimit}`} />
            <OperatorStatusRow label="Reset cadence" value={leaderboardResetCadence} />
          </div>

          <div className="mt-5 rounded-xl border border-white/[0.06] bg-black/20 p-4">
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border ${
                  activeLeaderboardCount > 0
                    ? 'bg-emerald-400/12 border-emerald-400/30 text-emerald-300'
                    : 'border-amber-400/25 bg-amber-400/10 text-amber-200'
                }`}
              >
                {activeLeaderboardCount > 0 ? (
                  <CheckIcon className="h-3.5 w-3.5" />
                ) : (
                  <PulseIcon className="h-3.5 w-3.5" />
                )}
              </div>
              <div>
                <div className="text-sm font-medium text-white">{leaderboardActionLabel}</div>
                <p className="mt-1 text-xs leading-5 text-gray-400">
                  {activeLeaderboardCount > 0
                    ? 'Visibility is already live. Use the full leaderboard workspace for deeper tuning.'
                    : inactiveLeaderboard
                      ? 'An existing board can be reactivated immediately from here.'
                      : 'This will create a sensible points leaderboard for the selected project.'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Link
              to="/ponzinomics/leaderboards"
              className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-white"
            >
              Open leaderboards
            </Link>
            <Button
              onClick={handleLeaderboardActivation}
              disabled={activeLeaderboardCount > 0 || isLeaderboardActionPending}
            >
              {isLeaderboardActionPending
                ? 'Saving...'
                : activeLeaderboardCount > 0
                  ? 'Leaderboard live'
                  : 'Activate Leaderboard'}
            </Button>
          </div>
        </div>
      </div>

      {recentQuests.length > 0 && (
        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex rounded-full border border-violet-400/20 bg-violet-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-violet-300">
                Quest Management
              </div>
              <h3 className="text-lg font-semibold text-white">Recent Quests</h3>
              <p className="mt-1 text-sm text-gray-500">
                Toggle status, edit points, or remove quests without leaving this page.
              </p>
            </div>
            <Link
              to="/ponzinomics/quests"
              className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-white"
            >
              Open quests
            </Link>
          </div>

          {quests && quests.length > 6 && (
            <div className="mb-3">
              <Input
                value={questSearchQuery}
                onChange={(e) => setQuestSearchQuery(e.target.value)}
                placeholder="Search by title, type, or status..."
              />
            </div>
          )}

          <div className="space-y-2">
            {recentQuests.map((quest) => (
              <div
                key={quest.id}
                className={`rounded-xl border transition-colors ${
                  editingQuestId === quest.id
                    ? 'border-violet-400/30 bg-violet-400/5'
                    : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.04]'
                }`}
              >
                {editingQuestId === quest.id ? (
                  <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                      <div className="flex-1">
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-gray-500">
                          Title
                        </label>
                        <Input
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          placeholder={quest.title}
                        />
                      </div>
                      <div className="w-full md:w-28">
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-gray-500">
                          Points
                        </label>
                        <Input
                          type="number"
                          min={1}
                          value={String(editPoints)}
                          onChange={(e) => setEditPoints(Number(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSaveQuestEdit(quest.id)}
                        disabled={updateQuest.isPending}
                      >
                        {updateQuest.isPending ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEditingQuest}
                        disabled={updateQuest.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-white">{quest.title}</span>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] ${
                            quest.status === 'active'
                              ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300'
                              : quest.status === 'paused'
                                ? 'border-amber-400/25 bg-amber-400/10 text-amber-300'
                                : 'border-gray-400/25 bg-gray-400/10 text-gray-400'
                          }`}
                        >
                          {quest.status}
                        </span>
                        <span className="rounded-full border border-white/[0.08] px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-gray-400">
                          {quest.type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                        <span>{quest.points} pts</span>
                        <span>{quest.completionCount} completions</span>
                        {quest.requirements.length > 0 && (
                          <span className="max-w-[200px] truncate text-gray-500">
                            {quest.requirements[0]?.value}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {quest.status !== 'active' ? (
                        <button
                          type="button"
                          onClick={() => handleQuickQuestStatusToggle(quest.id, 'active')}
                          className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-emerald-300 transition-colors hover:border-emerald-400/40 hover:bg-emerald-400/15"
                          disabled={updateQuest.isPending}
                        >
                          Activate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleQuickQuestStatusToggle(quest.id, 'draft')}
                          className="rounded-lg border border-amber-400/25 bg-amber-400/10 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-amber-300 transition-colors hover:border-amber-400/40 hover:bg-amber-400/15"
                          disabled={updateQuest.isPending}
                        >
                          Pause
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => startEditingQuest(quest)}
                        className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-gray-400 transition-colors hover:border-white/[0.16] hover:text-white"
                      >
                        Edit
                      </button>
                      {confirmDeleteQuestId === quest.id ? (
                        <>
                          <span className="text-xs text-amber-300">Confirm?</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteQuest(quest.id)}
                            className="rounded-lg bg-red-400/15 px-2.5 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-400/25"
                            disabled={deleteQuest.isPending}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={cancelDeleteQuest}
                            className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-gray-400 transition-colors hover:text-white"
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => requestDeleteQuest(quest.id)}
                          className="bg-red-400/8 hover:bg-red-400/12 rounded-lg border border-red-400/20 px-3 py-1.5 text-xs font-medium text-red-400 transition-colors hover:border-red-400/35"
                          disabled={deleteQuest.isPending}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Points Awarded"
          value={dashboardLoading ? '...' : formatCompactNumber(dashboardStats?.pointsAwarded ?? 0)}
          hint="Reward pressure"
          Icon={SparkIcon}
          colorClass="text-[#c4f70e]"
        />
        <MetricCard
          label="Leaderboard Configs"
          value={leaderboardLoading ? '...' : String(leaderboardConfigs.length)}
          hint={`${activeLeaderboardCount} active`}
          Icon={LeaderboardIcon}
          colorClass="text-yellow-400"
        />
      </div>

      {topPerformers.length > 0 && (
        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-amber-300">
                Top Performers
              </div>
              <h3 className="text-lg font-semibold text-white">Leaderboard Champions</h3>
              <p className="mt-1 text-sm text-gray-500">
                {leaderboardDataLoading
                  ? 'Loading rankings...'
                  : `Top 5 of ${totalParticipants} participant${totalParticipants === 1 ? '' : 's'} across all time.`}
              </p>
            </div>
            <Link
              to="/ponzinomics/leaderboards"
              className="text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-white"
            >
              Full board
            </Link>
          </div>

          <div className="space-y-2">
            {topPerformers.map((entry) => {
              const tierColor =
                entry.rank === 1
                  ? 'from-yellow-400/20 to-yellow-400/5 text-yellow-300 border-yellow-400/25'
                  : entry.rank === 2
                    ? 'from-gray-300/15 to-gray-300/5 text-gray-300 border-gray-300/20'
                    : entry.rank === 3
                      ? 'from-amber-600/15 to-amber-600/5 text-amber-400 border-amber-600/20'
                      : 'from-white/[0.04] to-white/[0.01] text-gray-400 border-white/[0.06]';

              return (
                <div
                  key={entry.userId}
                  className={`rounded-xl border bg-gradient-to-r p-4 ${tierColor}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                        entry.rank <= 3 ? 'bg-white/10' : 'bg-white/[0.06] text-gray-500'
                      }`}
                    >
                      {entry.rank}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-white">{entry.displayName}</span>
                        {entry.tier && (
                          <span className="rounded-full border border-white/[0.08] px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-gray-400">
                            {entry.tier}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                        <span>{formatCompactNumber(entry.points)} pts</span>
                        {entry.questsCompleted != null && (
                          <span>{entry.questsCompleted} quests</span>
                        )}
                        {entry.memesCreated != null && <span>{entry.memesCreated} memes</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {quests && quests.length > 0 && (
        <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-6 backdrop-blur-md">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex rounded-full border border-rose-400/20 bg-rose-400/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-rose-300">
                Performance Analytics
              </div>
              <h3 className="text-lg font-semibold text-white">Loop Health</h3>
              <p className="mt-1 text-sm text-gray-500">
                See where participation flows and where quests leak users.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <AnalyticTile
              label="Total Completions"
              value={formatCompactNumber(totalQuestCompletions)}
              hint="Across all quests"
            />
            <AnalyticTile
              label="Avg per Quest"
              value={formatCompactNumber(avgCompletionsPerQuest)}
              hint="Completions each"
            />
            <AnalyticTile
              label="Points Distributed"
              value={formatCompactNumber(totalQuestPointsOffered)}
              hint="Total rewards paid"
            />
            <AnalyticTile
              label="Quest Types"
              value={String(Object.keys(questTypeBreakdown).length)}
              hint={Object.entries(questTypeBreakdown)
                .map(([t, c]) => `${c} ${t.replace('_', ' ')}`)
                .join(' · ')}
            />
          </div>

          {topQuestsByCompletions.length > 0 && (
            <div className="mt-5">
              <div className="mb-3 text-xs uppercase tracking-[0.18em] text-gray-500">
                Top Performers
              </div>
              <div className="space-y-2">
                {topQuestsByCompletions.map((quest, i) => (
                  <div
                    key={quest.id}
                    className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-2.5"
                  >
                    <div
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        i === 0
                          ? 'bg-emerald-400/15 text-emerald-300'
                          : i === 1
                            ? 'bg-blue-400/15 text-blue-300'
                            : 'bg-violet-400/15 text-violet-300'
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-white">
                          {quest.title}
                        </span>
                        <span
                          className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] ${
                            quest.status === 'active'
                              ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300'
                              : 'border-gray-400/25 bg-gray-400/10 text-gray-400'
                          }`}
                        >
                          {quest.status}
                        </span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold text-white">
                        {formatCompactNumber(quest.completionCount)}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500">
                        completions
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {underperformingQuests.length > 0 && (
            <div className="mt-5">
              <div className="mb-3 text-xs uppercase tracking-[0.18em] text-amber-400/70">
                Needs Attention
              </div>
              <div className="space-y-2">
                {underperformingQuests.map((quest) => (
                  <div
                    key={quest.id}
                    className="flex items-center gap-3 rounded-lg border border-amber-400/15 bg-amber-400/5 px-4 py-2.5"
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-amber-400/25 bg-amber-400/10 text-amber-300">
                      <PulseIcon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-white">{quest.title}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({quest.type.replace('_', ' ')})
                      </span>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs text-amber-300">0 completions</div>
                      <div className="text-[10px] text-gray-500">Active but no traction</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {surfaces.map((surface) => (
          <Link
            key={surface.href}
            to={surface.href}
            className="notch-border-lg notch-border-white/8 notch-bg-white/3 hover:notch-border-white/20 group p-5 backdrop-blur-md transition-colors hover:[--notch-bg:rgba(255,255,255,0.05)]"
          >
            <div className={`mb-4 ${surface.colorClass}`}>
              <surface.Icon className="h-5 w-5" />
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-white">{surface.title}</h3>
                <p className="mt-1 text-xs leading-5 text-gray-400">{surface.description}</p>
              </div>
              <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-600 transition-all group-hover:translate-x-0.5 group-hover:text-white" />
            </div>
            <div className="mt-5 flex items-end justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-white">{surface.metric}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-gray-500">
                  {surface.status}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}): ReactElement {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-black/20 px-4 py-3">
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
      <div className="mt-1 text-xs text-gray-400">{hint}</div>
    </div>
  );
}

function AnalyticTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}): ReactElement {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3">
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
      <div className="mt-1 truncate text-xs text-gray-400">{hint}</div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  Icon,
  colorClass,
}: {
  label: string;
  value: string;
  hint: string;
  Icon: ({ className }: { className?: string }) => ReactElement;
  colorClass: string;
}): ReactElement {
  return (
    <div className="notch-border-lg notch-border-white/8 notch-bg-white/3 p-5 backdrop-blur-md">
      <div className={`mb-4 ${colorClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
      <div className="mt-1 text-xs text-gray-400">{hint}</div>
    </div>
  );
}

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function mapResetPeriodToTimeWindow(resetPeriod: 'never' | 'daily' | 'weekly' | 'monthly'): string {
  return resetPeriod === 'never' ? 'all-time' : resetPeriod;
}

function OperatorStatusRow({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <span className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</span>
      <span className="text-sm font-medium text-white">{value}</span>
    </div>
  );
}

interface CampaignQuestDraft {
  type: QuickQuestType;
  title: string;
  target: string;
  points: number;
}

function CampaignCreator({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}): ReactElement {
  const createQuest = useCreateQuest();
  const [quests, setQuests] = useState<CampaignQuestDraft[]>([
    { type: 'twitter_follow', title: '', target: '', points: 100 },
    { type: 'twitter_retweet', title: '', target: '', points: 100 },
  ]);
  const [errors, setErrors] = useState<string[]>([]);

  const updateQuest = (index: number, updates: Partial<CampaignQuestDraft>): void => {
    setQuests((prev) => prev.map((q, i) => (i === index ? { ...q, ...updates } : q)));
  };

  const addQuest = (): void => {
    if (quests.length < 4) {
      setQuests((prev) => [
        ...prev,
        { type: 'twitter_follow', title: '', target: '', points: 100 },
      ]);
    }
  };

  const removeQuest = (index: number): void => {
    if (quests.length > 2) {
      setQuests((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleLaunch = async (): Promise<void> => {
    const newErrors: string[] = [];
    quests.forEach((q, i) => {
      if (!q.title.trim()) newErrors.push(`Quest ${i + 1}: title required`);
      if (!q.target.trim()) newErrors.push(`Quest ${i + 1}: requirement required`);
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors([]);
    const descriptions: Record<QuickQuestType, string> = {
      twitter_follow: 'Follow the selected project accounts to earn points.',
      twitter_retweet: 'Retweet the selected post to earn points.',
      wallet_connect: 'Connect your wallet to participate.',
      token_hold: 'Hold the required token to earn points.',
      custom: 'Complete the action to earn points.',
    };

    try {
      await Promise.all(
        quests.map((q) =>
          createQuest.mutateAsync({
            title: q.title.trim(),
            description: descriptions[q.type],
            type: q.type,
            points: q.points,
            status: 'active',
            requirements: [{ type: q.type, value: q.target.trim().replace(/^@/, '') }],
          })
        )
      );
      toast({
        title: 'Campaign launched',
        description: `${quests.length} quests created and activated.`,
      });
      onCreated();
    } catch {
      toast({ title: 'Failed to launch campaign', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-400/20 bg-red-400/10 p-3">
          {errors.map((e) => (
            <p key={e} className="text-xs text-red-300">
              {e}
            </p>
          ))}
        </div>
      )}

      {quests.map((q, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 md:flex-row md:items-end"
        >
          <div className="flex-1">
            <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-gray-500">
              Quest {i + 1} — Type
            </label>
            <select
              value={q.type}
              onChange={(e) => updateQuest(i, { type: e.target.value as QuickQuestType })}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white outline-none transition-colors focus:border-[#c4f70e]/40"
            >
              {(Object.keys(QUEST_TYPE_LABELS) as QuickQuestType[]).map((type) => (
                <option key={type} value={type} className="bg-[#0d1117]">
                  {QUEST_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-gray-500">
              Title
            </label>
            <Input
              value={q.title}
              onChange={(e) => updateQuest(i, { title: e.target.value })}
              placeholder={QUEST_TYPE_TITLE_PLACEHOLDERS[q.type]}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-gray-500">
              {QUEST_TYPE_REQUIREMENT_LABELS[q.type]}
            </label>
            <Input
              value={q.target}
              onChange={(e) => updateQuest(i, { target: e.target.value })}
              placeholder={QUEST_TYPE_PLACEHOLDERS[q.type]}
            />
          </div>
          <div className="w-full md:w-20">
            <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-gray-500">
              Pts
            </label>
            <Input
              type="number"
              min={1}
              value={String(q.points)}
              onChange={(e) => updateQuest(i, { points: Number(e.target.value) || 0 })}
            />
          </div>
          {quests.length > 2 && (
            <button
              type="button"
              onClick={() => removeQuest(i)}
              className="rounded-lg px-2 py-2 text-xs text-red-400 transition-colors hover:text-red-300"
            >
              ✕
            </button>
          )}
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        {quests.length < 4 && (
          <button
            type="button"
            onClick={addQuest}
            className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-gray-400 transition-colors hover:border-white/[0.16] hover:text-white"
          >
            + Add quest
          </button>
        )}
        <div className="flex-1" />
        <Button variant="outline" onClick={onCancel} size="sm">
          Cancel
        </Button>
        <Button onClick={handleLaunch} disabled={createQuest.isPending} size="sm">
          {createQuest.isPending ? 'Launching...' : `Launch ${quests.length} Quests`}
        </Button>
      </div>
    </div>
  );
}

function QuestIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

function LeaderboardIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
      />
    </svg>
  );
}

function SparkIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m13.5 4.5 1.746 3.537 3.904.567-2.825 2.754.667 3.888L13.5 13.5l-3.492 1.746.667-3.888L7.85 8.604l3.904-.567L13.5 4.5Z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PulseIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h4l2-5 4 10 2-5h6" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
  );
}
